import React, {
    ReactElement,
} from "react";
import Editor from "../../../../components/agent-v3/editor/Editor";

import Select from "../../../../components/select/Select";

import {
    DeflectionTicketStatusAssignment,
    GetArtificialIntelligenceSettingsDocument,
    GetCurrentPortalDocument,
    GetSettingsDocument,
    IntegrationActionType,
    useGetArtificialIntelligenceObjectsQuery,
    useGetArtificialIntelligenceSettingsQuery,
    useSaveArtificialIntelligenceSettingsMutation,
    useSaveSettingsMutation,
} from "../../../../__generated__/api";
import { cloneWithoutTypename } from "../../../../api";
import { useFormBuilder } from "../../../../components/dirty-tracker/FormTracker";
import ArticleTags from "../../../../components/pickers/article-tags/Tags";
import Integrations from "../../../../components/pickers/integrations/Integrations";
import Alert from "../../../../components/theme/alert/Alert";
import FormGroup from "../../../../components/theme/form-group/FormGroup";
import SettingsContainer from "../../../../components/theme/settings";
import Toggle from "../../../../components/theme/toggle/Toggle";
import { LoggingHandler } from "../../../../contexts/logging/LoggingContext";
import usePortal from "../../../../contexts/PortalContext";
import { DEEP_COMPARER, SettingsProps } from "../base";

import Agents from "./Agent";
import MCPServer from "./MCPServer";

const DEFLECTION_ASSIGNMENT_STATUSES = {
    [DeflectionTicketStatusAssignment.ChatRequested]: "Show tickets to agents while deflection is waiting",
    [DeflectionTicketStatusAssignment.DeflectionWaiting]:
        "Do not show tickets to agents while deflection is waiting",
};

const FEATURE_FLAG_AI_ENABLED = "frontend.ai.enabled";

export default function ArtificialIntelligence(props: SettingsProps): ReactElement {
    const [save] = useSaveSettingsMutation();
    const [saveSettings] = useSaveArtificialIntelligenceSettingsMutation();
    const { entitlementIds } = usePortal();

    const deflection = props.settings?.artificialIntelligence?.deflection;

    const { data, loading: isDataLoading } = useGetArtificialIntelligenceSettingsQuery();

    const { featureFlags } = usePortal();

    const isLoading = props.isLoading || isDataLoading;

    const isAIAvailable = featureFlags.has(FEATURE_FLAG_AI_ENABLED);

    const { properties, tracker } = useFormBuilder(
        builder => builder
            .property("isEnabled", () => data?.artificialIntelligenceSettings?.isEnabled ?? false)
            .property("message", () => deflection?.message?.rawText ?? "")
            .property("llmProviderId", () => data?.artificialIntelligenceSettings?.llmProviderId)
            .property("ticketStatusAssignment", () => deflection?.deflectionTicketStatusAssignment
                ?? DeflectionTicketStatusAssignment.DeflectionWaiting)
            .property("tonePrompt", () => data?.artificialIntelligenceSettings?.tonePrompt ?? "")
            .object("features", () => ({
                isAskAiEnabled: true,
                isRecommendResponseEnabled: true,
                isCreateArticleEnabled: true,
                ...data?.artificialIntelligenceSettings?.features,
            }), DEEP_COMPARER)
            .proxy("articleProposals", () => data?.artificialIntelligenceSettings?.articleProposals),
        [deflection, data?.artificialIntelligenceSettings],
    );

    const articleProposalsProperties = properties.articleProposals.useBuilder(builder => builder
        .property("isEnabled", settings => settings?.isEnabled ?? false)
        .array("allowedTagIds", settings => settings?.allowedTagIds ?? []));

    const { element: saveElement } = tracker.useDirtyTracker({
        finishedSavingMessage: "Settings saved!",
        onSave: (__, onDone) => {
            LoggingHandler.instance.wrap(async () => {
                const message = properties.message.value;
                const deflectionTicketStatusAssignment = properties.ticketStatusAssignment.value;
                const isEnabled = properties.isEnabled.value;
                await save({
                    refetchQueries: [
                        { query: GetSettingsDocument },
                        { query: GetArtificialIntelligenceSettingsDocument },
                    ],
                    variables: {
                        artificialIntelligence: {
                            deflection: {
                                message: message ? {
                                    rawText: message,
                                } : undefined,
                                deflectionTicketStatusAssignment: deflectionTicketStatusAssignment
                                    ?? DeflectionTicketStatusAssignment.ChatRequested,
                            },

                        },
                    },
                });
                if (featureFlags.has(FEATURE_FLAG_AI_ENABLED)) {
                    await saveSettings({
                        refetchQueries: [
                            { query: GetArtificialIntelligenceSettingsDocument },
                            { query: GetCurrentPortalDocument },
                        ],
                        variables: {
                            settings: {
                                isEnabled: isEnabled ?? false,
                                llmProviderId: properties.llmProviderId.value,
                                tonePrompt: properties.tonePrompt.value,
                                features: cloneWithoutTypename(properties.features.value),
                                articleProposals: properties.articleProposals.serialize(),
                            },
                        },
                    });
                }
                onDone();
            });
        },
    });

    return (
        <>
            <SettingsContainer navigation={isAIAvailable ? "sections" : "none"}>
                {isAIAvailable && (
                    <SettingsContainer.Section
                        title="AI Assist"
                        helpText="AI assist helps agents use AI Features such as conversation summarization, rewording, etc."
                    >

                        <SettingsContainer.Setting
                            title="Enabled"
                            description="Should AI features be enabled. Keep in mind, AI features utilize
                                    3rd party partners such as OpenAI and, if enabled, will automatically send conversations,
                                    user names, and other data to the 3rd party. By enabling this feature you agree the
                                    terms of service of the 3rd party."
                            permissions={SettingsContainer.Setting.Permissions.Administrator}
                            isLoading={isLoading}
                        >
                            <Toggle
                                checked={properties.isEnabled.value ?? false}
                                size="medium"
                                onChange={event => properties.isEnabled.setValue(event.target.checked)}
                            />
                        </SettingsContainer.Setting>

                        <SettingsContainer.Setting
                            title="Model"
                            description="Which LLM Model services should Foqal use for AI features."
                            permissions={SettingsContainer.Setting.Permissions.Administrator}
                            isLoading={isLoading}
                            isVisible={properties.isEnabled.value}
                        >
                            <Integrations
                                placeholder="Which integration should create a ticket"
                                onSelected={properties.llmProviderId.setValue}
                                canSetNone
                                value={properties.llmProviderId.value}
                                actions={[IntegrationActionType.LlmProvider]}
                            />
                            {properties.llmProviderId.value && (
                                <Alert variant="danger" className="mt-1">
                                    By saving this setting, you agree to the terms of service
                                    and privacy policies of the selected LLM provider.
                                </Alert>
                            )}

                        </SettingsContainer.Setting>

                        <SettingsContainer.Setting
                            title="Tone"
                            description="The description of the tone that AI should use when responding
                                to customers and agents. For example, 'Professional, Friendly, Casual, etc.'"
                            permissions={SettingsContainer.Setting.Permissions.Administrator}
                            isVisible={properties.isEnabled.value}
                        >
                            <FormGroup
                                type="text"
                                display="textarea"
                                placeholder="Professional, Friendly, Casual, etc."
                                onChange={evt => properties.tonePrompt.setValue(evt.target.value)}
                                value={properties.tonePrompt.value ?? ""}
                                size="small"
                            />
                        </SettingsContainer.Setting>

                    </SettingsContainer.Section>
                )}
                <SettingsContainer.Section
                    title="Deflection"
                    helpText="Deflection is an AI feature which uses your knowledge base articles
                    to automatically answer users questions."
                >
                    <SettingsContainer.Setting
                        title="Deflection Message"
                        description="A custom message to show when deflection occurs. If
                        left empty the default message will display."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                    >
                        <Editor
                            value={properties.message.value ?? ""}
                            size="medium"
                            hasBorder
                            onChange={properties.message.setValue}
                            placeholder="Here are some articles that might help"
                        />
                    </SettingsContainer.Setting>
                    <SettingsContainer.Setting
                        title="Deflection Ticket Availability"
                        description="Should a ticket that is waiting for a deflection response be available to an agent?"
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                    >
                        <Select
                            className="text-gray"
                            placeholder=""
                            onChange={value => properties
                                .ticketStatusAssignment.setValue(value.value as DeflectionTicketStatusAssignment)}
                            options={
                                Object.entries(DEFLECTION_ASSIGNMENT_STATUSES).map(([value, label]) => ({ value, label }))
                            }
                            value={!properties.ticketStatusAssignment.value ? undefined : {
                                value: properties.ticketStatusAssignment.value,
                                label: DEFLECTION_ASSIGNMENT_STATUSES[properties.ticketStatusAssignment.value],
                            }}
                        />
                    </SettingsContainer.Setting>

                </SettingsContainer.Section>

                <SettingsContainer.Section
                    title="Answer Suggestions"
                    helpText="Foqal AI will analyze incoming messages and suggest new articles
                    to be stored in the knowledge base."
                >

                    <SettingsContainer.Setting
                        title="Enabled"
                        description="Allow Foqal AI to suggest new articles to be stored in the knowledge base."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isLoading={isLoading}
                        isVisible={properties.isEnabled.value}
                    >
                        <Toggle
                            checked={articleProposalsProperties.isEnabled?.value ?? true}
                            size="medium"
                            onChange={event => articleProposalsProperties.isEnabled.setValue(event.target.checked)}
                        />
                    </SettingsContainer.Setting>

                    <SettingsContainer.Setting
                        title="Allowed Tags"
                        description="When generating article, only the following tags will be considered."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isLoading={isLoading}
                        isVisible={properties.isEnabled.value && articleProposalsProperties.isEnabled.value}
                    >
                        <ArticleTags
                            isMulti
                            value={articleProposalsProperties.allowedTagIds.value}
                            onSelected={articleProposalsProperties.allowedTagIds.setValue}
                        />
                    </SettingsContainer.Setting>

                </SettingsContainer.Section>

                <SettingsContainer.Section
                    title="Features"
                    helpText="Which features are available for agents in the Foqal web portal."
                >

                    <SettingsContainer.Setting
                        title="Ask AI"
                        description={`Ask AI allows agents to ask questions about the ticket.
                                Common questions are like "What has the user already tried" or
                                give me more information about this ticket.`}
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isLoading={isLoading}
                        isVisible={properties.isEnabled.value}

                    >
                        <Toggle
                            checked={properties.features.value?.isAskAiEnabled ?? true}
                            size="medium"
                            onChange={event => properties.features.setValue({
                                ...properties.features.value,
                                isAskAiEnabled: event.target.checked,
                            })}
                        />
                    </SettingsContainer.Setting>

                    <SettingsContainer.Setting
                        title="Recommend Response"
                        description="Foqal will recommend responses directly in the ticket window
                                allowing users to 1-click respond to users."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isLoading={isLoading}
                        isVisible={properties.isEnabled.value}
                    >
                        <Toggle
                            checked={properties.features.value?.isRecommendResponseEnabled ?? true}
                            size="medium"
                            onChange={event => properties.features.setValue({
                                ...properties.features.value,
                                isRecommendResponseEnabled: event.target.checked,
                            })}
                        />
                    </SettingsContainer.Setting>

                    <SettingsContainer.Setting
                        title="Create Articles"
                        description="Allow agents to create articles from the ticketing view."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isLoading={isLoading}
                        isVisible={properties.isEnabled.value}
                    >
                        <Toggle
                            checked={properties.features.value?.isCreateArticleEnabled ?? true}
                            size="medium"
                            onChange={event => properties.features.setValue({
                                ...properties.features.value,
                                isCreateArticleEnabled: event.target.checked,
                            })}
                        />
                    </SettingsContainer.Setting>

                </SettingsContainer.Section>

                <Agents isEnabled={properties.isEnabled.value} />
                {entitlementIds.has(entitlementIds.keys.MCP_SERVERS) && (
                    <MCPServer isEnabled={properties.isEnabled.value} />
                )}

            </SettingsContainer>
            {saveElement}
        </>

    );
}
